const { UploadCache } = require('../../storage/upload-cache.js');
const logger = require('../../logger');
const { validateUploadId } = require('../utils/upload-validator');
const { isRetryableError } = require('../../storage/cache-errors.js');

/**
 * 缓存服务类
 */
class CacheService {
    constructor() {
        // UploadCache 是静态类，不需要实例化
        this.uploadCache = UploadCache;
        // 重试配置
        this.maxRetries = 3;
        this.baseRetryDelay = 100; // 100ms
    }

    /**
     * 带重试的操作执行
     * @param {Function} operation - 要执行的操作
     * @param {string} operationName - 操作名称（用于日志）
     * @param {number} retries - 已重试次数
     * @returns {Promise<any>}
     */
    async executeWithRetry(operation, operationName, retries = 0) {
        try {
            return await operation();
        } catch (error) {
            // 检查是否可重试
            if (!isRetryableError(error) || retries >= this.maxRetries) {
                logger.log(`[CacheService] ${operationName} failed after ${retries} retries:`, error.message);
                throw error;
            }

            // 计算延迟（指数退避）
            const delay = this.baseRetryDelay * Math.pow(2, retries);
            logger.log(
                `[CacheService] ${operationName} failed, retrying in ${delay}ms (attempt ${retries + 1}/${
                    this.maxRetries
                })`
            );

            // 等待后重试
            await new Promise((resolve) => setTimeout(resolve, delay));
            return this.executeWithRetry(operation, operationName, retries + 1);
        }
    }

    async findResumeRecord(teacherId, fileMD5) {
        if (!teacherId || !fileMD5) {
            return null;
        }

        try {
            return await this.uploadCache.findRecord(teacherId, fileMD5);
        } catch (error) {
            logger.log('[CacheService] Failed to find resume record:', error.message);
            return null;
        }
    }

    async saveUploadRecord(record) {
        if (!record.fileMD5) {
            return false;
        }

        try {
            const result = await this.uploadCache.saveRecord(record);
            logger.log('[CacheService] Upload record saved successfully', result);
            return result;
        } catch (error) {
            logger.errorAndLog('[CacheService] Failed to save upload record:', error.message);
            return false;
        }
    }

    async updatePartInfo(uploadId, partInfo) {
        if (!uploadId) {
            return false;
        }

        try {
            // updatePartInfo 使用重试机制
            const result = await this.executeWithRetry(
                () => this.uploadCache.updatePart(uploadId, partInfo),
                'updatePartInfo'
            );
            return result;
        } catch (error) {
            logger.errorAndLog('[CacheService] Failed to update part info:', error.message);
            return false;
        }
    }

    async getUploadRecord(uploadId) {
        if (!uploadId) {
            return null;
        }

        try {
            return await this.uploadCache.getRecord(uploadId);
        } catch (error) {
            logger.log('[CacheService] Failed to get upload record:', error.message);
            return null;
        }
    }

    async deleteUploadRecord(uploadId) {
        if (!uploadId) {
            return false;
        }

        try {
            const result = await this.uploadCache.deleteRecord(uploadId);
            return result;
        } catch (error) {
            logger.errorAndLog('[CacheService] Failed to delete upload record:', error.message);
            return false;
        }
    }

    async deleteUploadRecordByTeacherIdAndFileMD5(teacherId, fileMD5) {
        if (!teacherId || !fileMD5) {
            return false;
        }

        try {
            const result = await this.uploadCache.deleteRecordByTeacherIdAndFileMD5(teacherId, fileMD5);
            return result;
        } catch (error) {
            logger.errorAndLog('[CacheService] Failed to delete upload record:', error.message);
            return false;
        }
    }

    async validateUploadIdOnS3(s3Client, bucket, key, uploadId, abortSignal) {
        return await validateUploadId(s3Client, bucket, key, uploadId, abortSignal);
    }
}

module.exports = CacheService;
